import { useMemo, useState } from 'react';

import {
  useGetProjectUsersQuery,
  useGetUsersQuery,
  useInviteUsersToProjectMutation,
} from '@features/users';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { logger } from '@/utils/logger';

interface InviteProjectUsersModalProps {
  tenantId: string;
  appId: string;
  isOpen: boolean;
  onClose: () => void;
  identityIsAdmin: boolean;
}

// Email regex validation - stricter, more realistic
// Allows: letters, numbers, . _ % + - before @
// Requires valid domain with TLD (at least 2 letters)
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * Invite Users to Project Modal
 * Based on dataProtect userInviteModal.js (Lines 11-431)
 * Allows admin to invite users to a specific project with role selection
 */
export const InviteProjectUsersModal = ({
  tenantId,
  appId,
  isOpen,
  onClose,
  identityIsAdmin,
}: InviteProjectUsersModalProps) => {
  logger.log('🚀 InviteProjectUsersModal RENDER'); // DEBUG - Check if component renders

  // State management (dataProtect Lines 14-22)
  const [invitingUserIds, setInvitingUserIds] = useState<number[]>([]);
  const [invitingUserAdmins, setInvitingUserAdmins] = useState<Record<number, boolean>>({});
  const [emailInput, setEmailInput] = useState('');
  const [invitingUserEmails, setInvitingUserEmails] = useState<string[]>([]);
  const [invitingUserEmailAdmins, setInvitingUserEmailAdmins] = useState<Record<string, boolean>>(
    {}
  );
  const [validationError, setValidationError] = useState('');

  // API hooks (dataProtect Lines 35-40)
  const { data: allUsers = [] } = useGetUsersQuery();
  const { data: projectUsers = [] } = useGetProjectUsersQuery({ tenantId, appId });
  const [inviteUsers, { isLoading: isInviting }] = useInviteUsersToProjectMutation();

  // Filter org users (dataProtect Lines 197-208)
  const orgUsers = useMemo(
    () => allUsers.filter((u) => u.tenantid === tenantId),
    [allUsers, tenantId]
  );

  // Get uninvited org users - those not in project (dataProtect Lines 202-220)
  const uninvitedOrgUsers = useMemo(
    () =>
      orgUsers
        .filter((u) => {
          // Exclude if already a project member
          const isProjectMember = projectUsers.find((p) => p.id === u.id);
          if (isProjectMember) return false;

          // Exclude if already in inviting list
          if (invitingUserIds.includes(u.id)) return false;

          // Exclude current user
          return true;
        })
        .sort((a, b) => a.username.toLowerCase().localeCompare(b.username.toLowerCase())),
    [orgUsers, projectUsers, invitingUserIds]
  );

  // Get users to invite (those added to the list)
  const usersToInvite = useMemo(
    () =>
      orgUsers
        .filter((u) => invitingUserIds.includes(u.id))
        .sort((a, b) => a.username.toLowerCase().localeCompare(b.username.toLowerCase())),
    [orgUsers, invitingUserIds]
  );

  // Real-time email validation - computed directly on each render
  const getEmailError = (): string => {
    logger.log('🔍 [Project] Validating email:', emailInput); // DEBUG
    if (!emailInput) {
      logger.log('⚪ [Project] Empty input, no error'); // DEBUG
      return '';
    }

    // Check if already a project member
    const alreadyMember = projectUsers.find(
      (p) => p.username.toLowerCase() === emailInput.toLowerCase()
    );
    if (alreadyMember) {
      logger.log('❌ [Project] Already a member'); // DEBUG
      return 'The user is already a member of this Project';
    }

    // Check if already invited in this session (by ID or email)
    const invited = usersToInvite.find(
      (u) => u.username.toLowerCase() === emailInput.toLowerCase()
    );
    if (invited || invitingUserEmails.some((e) => e.toLowerCase() === emailInput.toLowerCase())) {
      logger.log('❌ [Project] Already in invite list'); // DEBUG
      return 'You have already added this email to the invite list';
    }

    // Check if user exists in org but not invited (this is OK, will be added by ID)
    const uninvited = uninvitedOrgUsers.find(
      (u) => u.username.toLowerCase() === emailInput.toLowerCase()
    );
    if (uninvited) {
      logger.log('✅ [Project] Valid - existing org user'); // DEBUG
      return ''; // Valid - existing org user
    }

    // For new emails, validate format
    if (!EMAIL_REGEX.test(emailInput)) {
      logger.log('❌ [Project] Failed EMAIL_REGEX test'); // DEBUG
      return 'Please enter a valid email address';
    }

    logger.log('✅ [Project] Email is valid'); // DEBUG
    return '';
  };

  const realTimeError = getEmailError();
  logger.log('💡 [Project] realTimeError value:', realTimeError); // DEBUG

  const handleClose = () => {
    onClose();
    setInvitingUserIds([]);
    setInvitingUserAdmins({});
    setEmailInput('');
    setInvitingUserEmails([]);
    setInvitingUserEmailAdmins({});
    setValidationError('');
  };

  // Add user by ID (dataProtect Lines 90-96)
  const inviteUser = (userId: number) => {
    setInvitingUserIds([...invitingUserIds, userId]);
    setInvitingUserAdmins({ ...invitingUserAdmins, [userId]: false });
  };

  // Remove user by ID (dataProtect Lines 97-106)
  const removeUser = (userId: number) => {
    setInvitingUserIds(invitingUserIds.filter((id) => id !== userId));
    const newAdmins = { ...invitingUserAdmins };
    delete newAdmins[userId];
    setInvitingUserAdmins(newAdmins);
  };

  // Remove email (dataProtect Lines 107-117)
  const removeEmailUser = (email: string) => {
    setInvitingUserEmails(invitingUserEmails.filter((e) => e !== email));
    const newEmailAdmins = { ...invitingUserEmailAdmins };
    delete newEmailAdmins[email];
    setInvitingUserEmailAdmins(newEmailAdmins);
  };

  // Update user admin state (dataProtect Lines 80-84)
  const updateInvitedUserAdmin = (userId: number, isAdmin: boolean) => {
    setInvitingUserAdmins({ ...invitingUserAdmins, [userId]: isAdmin });
  };

  // Update email user admin state (dataProtect Lines 85-89)
  const updateInvitedEmailUserAdmin = (email: string, isAdmin: boolean) => {
    setInvitingUserEmailAdmins({ ...invitingUserEmailAdmins, [email]: isAdmin });
  };

  // Check if user exists in project (dataProtect Lines 159-190)
  const checkIfUserExistsInProject = (email: string) => {
    // Check if already a project member
    const alreadyMember = projectUsers.find(
      (p) => p.username.toLowerCase() === email.toLowerCase()
    );

    // Check if already invited in this session
    const invited = usersToInvite.find((u) => u.username.toLowerCase() === email.toLowerCase());

    // Check if user exists in org but not invited
    const uninvited = uninvitedOrgUsers.find(
      (u) => u.username.toLowerCase() === email.toLowerCase()
    );

    return { uninvited, invited, alreadyMember };
  };

  // Add email to send list (dataProtect Lines 118-157)
  const addEmailToSendList = () => {
    setValidationError('');

    const check = checkIfUserExistsInProject(emailInput);

    // Already a project member (dataProtect Lines 128-132)
    if (check.alreadyMember) {
      setValidationError('The user is already a member of this Project');
      return;
    }

    // Already invited in this session (dataProtect Lines 134-139)
    if (check.invited || invitingUserEmails.includes(emailInput.toLowerCase())) {
      setValidationError('You have already added this email to the invite list');
      return;
    }

    // Existing org user, add by ID (dataProtect Lines 140-143)
    if (check.uninvited) {
      inviteUser(check.uninvited.id);
      setEmailInput('');
      return;
    }

    // New email, validate format (dataProtect Lines 145-157)
    if (!EMAIL_REGEX.test(emailInput)) {
      setValidationError('Please enter a valid email address');
      return;
    }

    // Add to email invite list
    setInvitingUserEmails([...invitingUserEmails, emailInput]);
    setInvitingUserEmailAdmins({ ...invitingUserEmailAdmins, [emailInput]: false });
    setEmailInput('');
  };

  // Final invite submission (dataProtect Lines 52-79)
  const finalInvite = async () => {
    // Build user data (existing org users by ID)
    const userData: Array<{ userId: number; isAdmin: boolean }> = invitingUserIds.map((uid) => ({
      userId: uid,
      isAdmin: invitingUserAdmins[uid] || false,
    }));

    // Build email data (new users by email)
    const emailData: Array<{ email: string; isAdmin: boolean }> = invitingUserEmails.map(
      (email) => ({
        email,
        isAdmin: invitingUserEmailAdmins[email] || false,
      })
    );

    // Combine and submit (types are compatible with InviteUsersRequest union)
    const payload = [...userData, ...emailData];

    try {
      await inviteUsers({
        tenantId,
        appId,
        users: payload,
      }).unwrap();

      handleClose();
    } catch (err: unknown) {
      const apiErr = err as { status?: number; data?: { message?: string } };
      logger.error('Failed to invite users:', err);

      // Handle specific error cases
      if (apiErr?.status === 403) {
        alert(
          "You don't have permission to invite users to this project.\n\n" +
            'Only project administrators can invite new members.'
        );
      } else if (apiErr?.data?.message) {
        alert(`Failed to send invitations: ${apiErr.data.message}`);
      } else {
        alert('Failed to send invitations. Please try again.');
      }
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={(_event, reason) => {
        // Only close when button is clicked, not on backdrop click or escape
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      disableScrollLock
      disableEscapeKeyDown
      slotProps={{
        paper: {
          sx: {
            height: '525px', // Match dataProtect height (Line 227)
          },
        },
      }}
    >
      <DialogTitle>
        <strong>Invite users to this Project [UPDATED CODE]</strong>
      </DialogTitle>

      <DialogContent sx={{ overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Access Roles Info (dataProtect Lines 238-247) */}
          {(usersToInvite.length > 0 || invitingUserEmails.length > 0) && (
            <Alert severity="info">
              <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                <strong>About access roles:</strong>
              </Typography>
              <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                <strong>Admin:</strong> Admin have the highest level of access to an
                Organization/Project. ie. Admin can <strong>view/invite/edit/remove</strong> other
                users having any of the access role.
              </Typography>
              <Typography variant="caption" component="div">
                <strong>User:</strong> User have the lowest level of access to an
                Organization/Project. ie. User can only <strong>view</strong> other users and{' '}
                <strong>invite</strong> others with user access role only.
              </Typography>
            </Alert>
          )}

          {/* Users to Invite List (dataProtect Lines 248-283) */}
          {(usersToInvite.length > 0 || invitingUserEmails.length > 0) && (
            <Box>
              <Typography variant="body2" gutterBottom>
                <strong>Users to invite:</strong>
              </Typography>
              <List dense sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
                {/* Existing org users */}
                {usersToInvite.map((user) => (
                  <ListItem
                    key={user.id}
                    secondaryAction={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption">
                            {invitingUserAdmins[user.id] ? 'Admin' : 'User'}
                          </Typography>
                          <Switch
                            checked={identityIsAdmin ? invitingUserAdmins[user.id] : false}
                            disabled={!identityIsAdmin}
                            onChange={(e) => updateInvitedUserAdmin(user.id, e.target.checked)}
                            size="small"
                          />
                        </Box>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => removeUser(user.id)}
                          title="Remove"
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText primary={user.username} />
                  </ListItem>
                ))}

                {/* New email users */}
                {invitingUserEmails.sort().map((email) => (
                  <ListItem
                    key={email}
                    secondaryAction={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption">
                            {invitingUserEmailAdmins[email] ? 'Admin' : 'User'}
                          </Typography>
                          <Switch
                            checked={identityIsAdmin ? invitingUserEmailAdmins[email] : false}
                            disabled={!identityIsAdmin}
                            onChange={(e) => updateInvitedEmailUserAdmin(email, e.target.checked)}
                            size="small"
                          />
                        </Box>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => removeEmailUser(email)}
                          title="Remove"
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText primary={email} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Divider />

          {/* Email Input (dataProtect Lines 285-325) */}
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>Invite new members by Email:</strong>
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                type="email"
                placeholder="Enter email address"
                value={emailInput}
                onChange={(e) => {
                  const newEmail = e.target.value.replace(/ /g, '').toLowerCase();
                  logger.log('📝 onChange called, newEmail:', newEmail); // DEBUG
                  setEmailInput(newEmail);
                  setValidationError('');
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addEmailToSendList();
                  }
                }}
                error={!!realTimeError}
                helperText={realTimeError || ' '}
                fullWidth
                size="small"
                slotProps={{
                  formHelperText: {
                    sx: { minHeight: '20px', margin: '4px 0 0 0' },
                  },
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={addEmailToSendList}
                disabled={!emailInput || !!realTimeError}
                sx={{ minWidth: 80 }}
              >
                Add
              </Button>
            </Box>
            {validationError && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                {validationError}
              </Typography>
            )}
          </Box>

          {/* Org Members Selector (dataProtect Lines 327-347) */}
          {uninvitedOrgUsers.length > 0 && (
            <Box>
              <Typography variant="body2" gutterBottom>
                <strong>Invite members from this Organization:</strong>
              </Typography>
              <Box
                sx={{
                  maxHeight: '150px',
                  overflowY: 'auto',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1,
                }}
              >
                {uninvitedOrgUsers.map((user) => (
                  <Box
                    key={user.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 0.5,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => inviteUser(user.id)}
                      sx={{
                        width: 21,
                        height: 21,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 0,
                        mr: 0.5,
                        '&:hover': {
                          bgcolor: 'primary.main',
                          color: 'white',
                        },
                      }}
                    >
                      <AddIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <Typography variant="body2">{user.username}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="error" onClick={handleClose} disabled={isInviting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={finalInvite}
          disabled={isInviting || (invitingUserIds.length === 0 && invitingUserEmails.length === 0)}
          sx={{ minWidth: 60 }}
        >
          {isInviting ? <CircularProgress size={20} /> : 'Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
